import { Suspense, lazy, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/hooks/useAuth";
import { useAdminCheck } from "@/hooks/useAdminCheck";
import { useCurrency } from "@/hooks/useCurrency";
import { supabase } from "@/integrations/supabase/client";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Users, LayoutGrid, DollarSign, Mail, ArrowLeft, Search, Palette, Music, Settings, Gift, ClipboardList, CircleDollarSign, CreditCard, Smile, AlertCircle } from "lucide-react";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";
import UserDetailModal from "@/components/admin/UserDetailModal";
import UserMenu from "@/components/UserMenu";
import { GridSkeleton, ListSkeleton, TableSkeleton, FormSkeleton } from "@/components/admin/AdminSkeletons";
import { useIsMobile } from "@/hooks/use-mobile";
import { format } from "date-fns";

// Lazy-load every heavy admin tab so switching between tabs doesn't
// eager-load the entire admin bundle on first visit. Each tab owns its
// own Supabase queries, UI, and modals — so splitting them keeps the
// initial /admin route fast and lets React Query cache survive tab
// switches across the whole admin session.
const TemplatesTab = lazy(() => import("@/components/admin/TemplatesTab"));
const MusicTab = lazy(() => import("@/components/admin/MusicTab"));
const SettingsTab = lazy(() => import("@/components/admin/SettingsTab"));
const WishlistsTab = lazy(() => import("@/components/admin/WishlistsTab"));
const AuditTab = lazy(() => import("@/components/admin/AuditTab"));
const PaymentsTab = lazy(() => import("@/components/admin/PaymentsTab"));
const StickersTab = lazy(() => import("@/components/admin/StickersTab"));
const ErrorsTab = lazy(() => import("@/components/admin/ErrorsTab"));


const PAGE_SIZE = 20;

/* ── Section Header ── */
const SectionHeader = ({ title, description, count }: { title: string; description: string; count?: number }) => (
  <div className="mb-4">
    <div className="flex items-baseline gap-2">
      <h2 className="text-base font-semibold">{title}</h2>
      {count !== undefined && (
        <Badge variant="secondary" className="text-[10px] font-normal">{count}</Badge>
      )}
    </div>
    <p className="text-xs text-muted-foreground mt-0.5">{description}</p>
  </div>
);

/* ── Stat Card ── */
const MiniStat = ({ label, value }: { label: string; value: string | number }) => (
  <Card className="p-0">
    <CardContent className="px-3 py-2.5 text-center">
      <p className="text-lg font-bold leading-tight">{value}</p>
      <p className="text-[10px] text-muted-foreground leading-tight mt-0.5">{label}</p>
    </CardContent>
  </Card>
);

const Admin = () => {
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();
  const { isAdmin, loading: adminLoading } = useAdminCheck();
  useEffect(() => {
    if (!authLoading && !adminLoading) {
      if (!user) navigate("/auth");
      else if (!isAdmin) navigate("/dashboard");
    }
  }, [user, isAdmin, authLoading, adminLoading, navigate]);

  if (authLoading || adminLoading) {
    return <div className="min-h-screen flex items-center justify-center text-muted-foreground">Loading…</div>;
  }
  if (!isAdmin) return null;

  return (
    <div className="min-h-screen bg-background">
      <header className="border-b bg-card">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-3 flex items-center gap-3">
          <Button variant="ghost" size="icon" onClick={() => navigate("/dashboard")}>
            <ArrowLeft size={18} />
          </Button>
          <h1 className="text-lg font-semibold">Admin</h1>
          {/* Spacer + user menu — every other page in the app shows the
              avatar in the top-right, so the admin view mirrors that
              pattern instead of dead-ending when users want to jump
              back to their profile/cards. */}
          <div className="flex-1" />
          <UserMenu />
        </div>
      </header>

      <div className="max-w-7xl mx-auto p-4">
        <Tabs defaultValue="users" className="space-y-4">
          <TabsList className="w-full justify-start overflow-x-auto flex-nowrap gap-0.5 h-auto p-1">
            <TabsTrigger value="users" className="gap-1 text-xs shrink-0"><Users size={13} /> Users</TabsTrigger>
            <TabsTrigger value="collections" className="gap-1 text-xs shrink-0"><LayoutGrid size={13} /> Cards</TabsTrigger>
            <TabsTrigger value="contributions" className="gap-1 text-xs shrink-0"><DollarSign size={13} /> Money</TabsTrigger>
            <TabsTrigger value="templates" className="gap-1 text-xs shrink-0"><Palette size={13} /> Templates</TabsTrigger>
            <TabsTrigger value="stickers" className="gap-1 text-xs shrink-0"><Smile size={13} /> Stickers</TabsTrigger>
            <TabsTrigger value="music" className="gap-1 text-xs shrink-0"><Music size={13} /> Music</TabsTrigger>
            <TabsTrigger value="wishlists" className="gap-1 text-xs shrink-0"><Gift size={13} /> Wishes</TabsTrigger>
            <TabsTrigger value="emails" className="gap-1 text-xs shrink-0"><Mail size={13} /> Emails</TabsTrigger>
            <TabsTrigger value="settings" className="gap-1 text-xs shrink-0"><Settings size={13} /> Config</TabsTrigger>
            <TabsTrigger value="payments" className="gap-1 text-xs shrink-0"><CreditCard size={13} /> Payments</TabsTrigger>
            <TabsTrigger value="audit" className="gap-1 text-xs shrink-0"><ClipboardList size={13} /> Audit</TabsTrigger>
            <TabsTrigger value="errors" className="gap-1 text-xs shrink-0"><AlertCircle size={13} /> Errors</TabsTrigger>
          </TabsList>

          <TabsContent value="users"><UsersTab /></TabsContent>
          <TabsContent value="collections"><CollectionsTab /></TabsContent>
          <TabsContent value="contributions"><ContributionsTab /></TabsContent>
          <TabsContent value="templates">
            <Suspense fallback={<GridSkeleton />}><TemplatesTab /></Suspense>
          </TabsContent>
          <TabsContent value="stickers">
            <Suspense fallback={<GridSkeleton aspect="aspect-square" />}><StickersTab /></Suspense>
          </TabsContent>
          <TabsContent value="music">
            <Suspense fallback={<ListSkeleton />}><MusicTab /></Suspense>
          </TabsContent>
          <TabsContent value="wishlists">
            <Suspense fallback={<ListSkeleton avatar />}><WishlistsTab /></Suspense>
          </TabsContent>
          <TabsContent value="emails"><EmailsTab /></TabsContent>
          <TabsContent value="settings">
            <Suspense fallback={<FormSkeleton />}><SettingsTab /></Suspense>
          </TabsContent>
          <TabsContent value="payments">
            <Suspense fallback={<TableSkeleton />}><PaymentsTab /></Suspense>
          </TabsContent>
          <TabsContent value="audit">
            <Suspense fallback={<TableSkeleton columns={5} />}><AuditTab /></Suspense>
          </TabsContent>
          <TabsContent value="errors">
            <Suspense fallback={<TableSkeleton columns={5} />}><ErrorsTab /></Suspense>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

/* ── Users Tab ── */
const UsersTab = () => {
  const isMobile = useIsMobile();
  const [profiles, setProfiles] = useState<any[]>([]);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [count, setCount] = useState(0);
  const [page, setPage] = useState(0);
  const [selectedProfile, setSelectedProfile] = useState<any | null>(null);
  const [emailVerified, setEmailVerified] = useState<Record<string, boolean>>({});

  const fetchProfiles = async () => {
    setLoading(true);
    let query = supabase
      .from("profiles")
      .select("*", { count: "exact" })
      .order("created_at", { ascending: false })
      .range(page * PAGE_SIZE, (page + 1) * PAGE_SIZE - 1);

    if (search) {
      query = query.or(`display_name.ilike.%${search}%,email.ilike.%${search}%`);
    }
    const { data, count: c } = await query;
    const profilesList = data || [];
    setProfiles(profilesList);
    setCount(c || 0);
    setLoading(false);

    // Fetch email verification status from auth
    if (profilesList.length > 0) {
      const userIds = profilesList.map((p: any) => p.user_id);
      const { data: verificationData } = await supabase.functions.invoke("admin-get-email-verification", {
        body: { user_ids: userIds },
      });
      if (verificationData) {
        const map: Record<string, boolean> = {};
        for (const [uid, info] of Object.entries(verificationData as Record<string, { email_confirmed: boolean; banned_until: string | null }>)) {
          map[uid] = info.email_confirmed;
        }
        setEmailVerified(map);
      }
    }
  };

  useEffect(() => { fetchProfiles(); }, [page, search]);

  const isVerified = (p: any) => emailVerified[p.user_id] ?? false;

  return (
    <div className="space-y-4">
      <SectionHeader title="Users" description="All registered accounts on the platform." count={count} />

      <div className="relative">
        <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
        <Input
          placeholder="Search by name or email…"
          value={search}
          onChange={e => { setSearch(e.target.value); setPage(0); }}
          className="pl-9"
        />
      </div>

      {loading ? (
        <ListSkeleton count={PAGE_SIZE} avatar />
      ) : profiles.length === 0 ? (
        <div className="text-center py-8 text-muted-foreground">No users found</div>
      ) : isMobile ? (
        <div className="space-y-2">
          {profiles.map(p => {
            const initials = (p.display_name || p.email || "?").split(" ").map((w: string) => w[0]).join("").toUpperCase().slice(0, 2);
            const verified = isVerified(p);
            return (
              <Card key={p.id} className="p-3 cursor-pointer active:bg-muted/50" onClick={() => setSelectedProfile({ ...p, _emailVerified: verified })}>
                <div className="flex items-center gap-3">
                  <Avatar className="h-10 w-10 shrink-0">
                    <AvatarImage src={p.avatar_url} />
                    <AvatarFallback className="text-xs">{initials}</AvatarFallback>
                  </Avatar>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center justify-between gap-2">
                      <p className="font-medium text-sm truncate">{p.display_name || "—"}</p>
                      <Badge variant={verified ? "default" : "secondary"} className="text-[10px] px-1.5 py-0 shrink-0">
                        {verified ? "Verified" : "Unverified"}
                      </Badge>
                    </div>
                    <p className="text-xs text-muted-foreground truncate mt-0.5">{p.email || "—"}</p>
                    <p className="text-[10px] text-muted-foreground mt-1">Joined {format(new Date(p.created_at), "MMM d, yyyy")}</p>
                  </div>
                </div>
              </Card>
            );
          })}
        </div>
      ) : (
        <Card>
          <CardContent className="p-0 overflow-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead></TableHead>
                  <TableHead>Name</TableHead>
                  <TableHead>Email</TableHead>
                  <TableHead>Joined</TableHead>
                  <TableHead>Verified</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {profiles.map(p => {
                  const initials = (p.display_name || p.email || "?").split(" ").map((w: string) => w[0]).join("").toUpperCase().slice(0, 2);
                  const verified = isVerified(p);
                  return (
                    <TableRow key={p.id} className="cursor-pointer hover:bg-muted/50" onClick={() => setSelectedProfile({ ...p, _emailVerified: verified })}>
                      <TableCell className="w-10 pr-0">
                        <Avatar className="h-8 w-8">
                          <AvatarImage src={p.avatar_url} />
                          <AvatarFallback className="text-xs">{initials}</AvatarFallback>
                        </Avatar>
                      </TableCell>
                      <TableCell className="font-medium">{p.display_name || "—"}</TableCell>
                      <TableCell className="text-muted-foreground text-sm">{p.email || "—"}</TableCell>
                      <TableCell className="text-sm">{format(new Date(p.created_at), "MMM d, yyyy")}</TableCell>
                      <TableCell>
                        <Badge variant={verified ? "default" : "secondary"}>
                          {verified ? "Yes" : "No"}
                        </Badge>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}

      <Pagination page={page} setPage={setPage} total={count} pageSize={PAGE_SIZE} />

      <UserDetailModal
        profile={selectedProfile}
        open={!!selectedProfile}
        onOpenChange={(open) => { if (!open) setSelectedProfile(null); }}
        onProfileUpdated={fetchProfiles}
      />
    </div>
  );
};

/* ── Collections Tab ── */
const CollectionsTab = () => {
  const isMobile = useIsMobile();
  const [collections, setCollections] = useState<any[]>([]);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [count, setCount] = useState(0);
  const [page, setPage] = useState(0);
  const [profileMap, setProfileMap] = useState<Record<string, string>>({});
  const [togglingId, setTogglingId] = useState<string | null>(null);
  const [statusCounts, setStatusCounts] = useState<Record<string, number>>({});

  const fetchCollections = async () => {
    setLoading(true);
    let query = supabase
      .from("collections")
      .select("*", { count: "exact" })
      .order("created_at", { ascending: false })
      .range(page * PAGE_SIZE, (page + 1) * PAGE_SIZE - 1);

    if (search) {
      query = query.or(`title.ilike.%${search}%,recipient_name.ilike.%${search}%`);
    }
    const { data, count: c } = await query;
    setCollections(data || []);
    setCount(c || 0);

    // Count by status
    const counts: Record<string, number> = {};
    (data || []).forEach((d: any) => { counts[d.status] = (counts[d.status] || 0) + 1; });
    setStatusCounts(counts);

    const organizerIds = [...new Set((data || []).map((d: any) => d.organizer_id).filter(Boolean))];
    if (organizerIds.length > 0) {
      const { data: profiles } = await supabase
        .from("profiles")
        .select("user_id, display_name, email")
        .in("user_id", organizerIds);
      const map: Record<string, string> = {};
      (profiles || []).forEach((p: any) => { map[p.user_id] = p.display_name || p.email || "Unknown"; });
      setProfileMap(map);
    }
    setLoading(false);
  };

  useEffect(() => { fetchCollections(); }, [page, search]);

  const toggleStatus = async (id: string, currentStatus: string) => {
    const newStatus = currentStatus === "closed" ? "active" : "closed";
    setTogglingId(id);
    try {
      const res = await supabase.functions.invoke("admin-update-collection-status", {
        body: { collection_id: id, status: newStatus },
      });
      if (res.error) throw res.error;
      setCollections(prev => prev.map(c => c.id === id ? { ...c, status: newStatus } : c));
      setStatusCounts(prev => ({
        ...prev,
        [currentStatus]: (prev[currentStatus] || 1) - 1,
        [newStatus]: (prev[newStatus] || 0) + 1,
      }));
    } catch (err: any) {
      console.error(err);
    } finally {
      setTogglingId(null);
    }
  };

  const [togglingChipIn, setTogglingChipIn] = useState<string | null>(null);

  const toggleChipIn = async (id: string, currentEnabled: boolean) => {
    setTogglingChipIn(id);
    try {
      const res = await supabase.functions.invoke("admin-update-collection-status", {
        body: { collection_id: id, collection_enabled: !currentEnabled },
      });
      if (res.error) throw res.error;
      setCollections(prev => prev.map(c => c.id === id ? { ...c, collection_enabled: !currentEnabled } : c));
    } catch (err: any) {
      console.error(err);
    } finally {
      setTogglingChipIn(null);
    }
  };

  const statusColor = (s: string) => {
    switch (s) {
      case "active": return "default";
      case "draft": return "secondary";
      case "closed": return "outline";
      case "delivered": return "default";
      default: return "secondary";
    }
  };

  return (
    <div className="space-y-4">
      <SectionHeader title="Group Cards" description="All cards created on the platform." count={count} />

      <div className="grid grid-cols-4 gap-2">
        <MiniStat label="Active" value={statusCounts.active || 0} />
        <MiniStat label="Draft" value={statusCounts.draft || 0} />
        <MiniStat label="Closed" value={statusCounts.closed || 0} />
        <MiniStat label="Delivered" value={statusCounts.delivered || 0} />
      </div>

      <div className="relative">
        <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
        <Input
          placeholder="Search by title or recipient…"
          value={search}
          onChange={e => { setSearch(e.target.value); setPage(0); }}
          className="pl-9"
        />
      </div>

      {loading ? (
        <ListSkeleton count={PAGE_SIZE} />
      ) : collections.length === 0 ? (
        <div className="text-center py-8 text-muted-foreground">No cards found</div>
      ) : isMobile ? (
        <div className="space-y-2">
          {collections.map(c => (
            <Card key={c.id} className="p-3">
              <div className="flex items-center justify-between gap-2 mb-2">
                <div className="flex items-center gap-1.5 min-w-0 flex-1">
                  <p className="font-medium text-sm truncate">{c.title}</p>
                  {c.collection_enabled && (
                    <CircleDollarSign size={14} className="text-green-600 shrink-0" />
                  )}
                </div>
                <Badge variant={statusColor(c.status)} className="text-[10px] px-1.5 py-0 shrink-0 capitalize">{c.status}</Badge>
              </div>
              <div className="grid grid-cols-3 gap-x-3 text-xs text-muted-foreground">
                <div>
                  <p className="text-[10px] uppercase tracking-wide text-muted-foreground/60 mb-0.5">Recipient</p>
                  <p className="truncate">{c.recipient_name}</p>
                </div>
                <div>
                  <p className="text-[10px] uppercase tracking-wide text-muted-foreground/60 mb-0.5">Occasion</p>
                  <p className="capitalize truncate">{c.occasion}</p>
                </div>
                <div>
                  <p className="text-[10px] uppercase tracking-wide text-muted-foreground/60 mb-0.5">Chip-in</p>
                  <p>{c.collection_enabled ? "Active" : "Off"}</p>
                </div>
              </div>
              <div className="flex items-center justify-between mt-2 pt-2 border-t border-border/50">
                <p className="text-[11px] text-muted-foreground truncate">By: {profileMap[c.organizer_id] || "—"}</p>
                <div className="flex gap-1 shrink-0">
                  <Button
                    variant="ghost"
                    size="sm"
                    className={`text-[10px] h-6 px-2 ${c.collection_enabled ? "text-destructive" : "text-primary"}`}
                    disabled={togglingChipIn === c.id}
                    onClick={() => toggleChipIn(c.id, c.collection_enabled)}
                  >
                    {togglingChipIn === c.id ? "…" : c.collection_enabled ? "Disable Chip-in" : "Enable Chip-in"}
                  </Button>
                  {(c.status === "active" || c.status === "closed") && (
                    <Button
                      variant="ghost"
                      size="sm"
                      className={`text-[10px] h-6 px-2 ${c.status === "active" ? "text-destructive" : "text-primary"}`}
                      disabled={togglingId === c.id}
                      onClick={() => toggleStatus(c.id, c.status)}
                    >
                      {togglingId === c.id ? "…" : c.status === "active" ? "Deactivate" : "Reactivate"}
                    </Button>
                  )}
                </div>
              </div>
            </Card>
          ))}
        </div>
      ) : (
        <Card>
          <CardContent className="p-0 overflow-auto">
            <Table>
              <TableHeader>
               <TableRow>
                   <TableHead>Title</TableHead>
                   <TableHead>Organizer</TableHead>
                   <TableHead>Recipient</TableHead>
                   <TableHead>Occasion</TableHead>
                   <TableHead>Chip-in</TableHead>
                   <TableHead>Status</TableHead>
                   <TableHead>Created</TableHead>
                   <TableHead className="w-[120px]">Action</TableHead>
                 </TableRow>
               </TableHeader>
               <TableBody>
                 {collections.map(c => (
                   <TableRow key={c.id}>
                     <TableCell className="font-medium">{c.title}</TableCell>
                     <TableCell className="text-sm text-muted-foreground">{profileMap[c.organizer_id] || "—"}</TableCell>
                     <TableCell>{c.recipient_name}</TableCell>
                     <TableCell className="capitalize">{c.occasion}</TableCell>
                     <TableCell>
                       <Badge variant={c.collection_enabled ? "default" : "secondary"} className="text-[10px]">
                         {c.collection_enabled ? "Active" : "Off"}
                       </Badge>
                     </TableCell>
                     <TableCell><Badge variant={statusColor(c.status)}>{c.status}</Badge></TableCell>
                     <TableCell className="text-sm">{format(new Date(c.created_at), "MMM d, yyyy")}</TableCell>
                     <TableCell>
                       <div className="flex gap-1">
                          <Button
                            variant="ghost"
                            size="sm"
                            className={`text-xs h-7 ${c.collection_enabled ? "text-destructive" : "text-primary"}`}
                            disabled={togglingChipIn === c.id}
                            onClick={() => toggleChipIn(c.id, c.collection_enabled)}
                          >
                            {togglingChipIn === c.id ? "…" : c.collection_enabled ? "Disable $" : "Enable $"}
                          </Button>
                         {(c.status === "active" || c.status === "closed") && (
                           <Button
                             variant="ghost"
                             size="sm"
                             className={`text-xs h-7 ${c.status === "active" ? "text-destructive" : "text-primary"}`}
                             disabled={togglingId === c.id}
                             onClick={() => toggleStatus(c.id, c.status)}
                           >
                             {togglingId === c.id ? "…" : c.status === "active" ? "Deactivate" : "Reactivate"}
                           </Button>
                         )}
                       </div>
                     </TableCell>
                   </TableRow>
                 ))}
               </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}

      <Pagination page={page} setPage={setPage} total={count} pageSize={PAGE_SIZE} />
    </div>
  );
};

/* ── Contributions Tab ── */
const ContributionsTab = () => {
  const isMobile = useIsMobile();
  const currency = useCurrency();
  const [contributions, setContributions] = useState<any[]>([]);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [count, setCount] = useState(0);
  const [page, setPage] = useState(0);
  const [totalCollected, setTotalCollected] = useState(0);
  const [completedCount, setCompletedCount] = useState(0);
  const [cardMap, setCardMap] = useState<Record<string, string>>({});

  const fetchContributions = async () => {
    setLoading(true);
    let query = supabase
      .from("contributions")
      .select("*", { count: "exact" })
      .order("created_at", { ascending: false })
      .range(page * PAGE_SIZE, (page + 1) * PAGE_SIZE - 1);

    if (search) {
      query = query.or(`contributor_name.ilike.%${search}%,contributor_email.ilike.%${search}%`);
    }

    const { data, count: c } = await query;
    setContributions(data || []);
    setCount(c || 0);

    const collectionIds = [...new Set((data || []).map((d: any) => d.collection_id).filter(Boolean))];
    if (collectionIds.length > 0) {
      const { data: cols } = await supabase
        .from("collections")
        .select("id, title")
        .in("id", collectionIds);
      const map: Record<string, string> = {};
      (cols || []).forEach((col: any) => { map[col.id] = col.title; });
      setCardMap(map);
    }

    const { data: totals, count: completedC } = await supabase
      .from("contributions")
      .select("amount", { count: "exact" })
      .eq("status", "completed");
    const sum = (totals || []).reduce((acc, r) => acc + Number(r.amount), 0);
    setTotalCollected(sum);
    setCompletedCount(completedC || 0);
    setLoading(false);
  };

  useEffect(() => { fetchContributions(); }, [page, search]);

  const statusColor = (s: string) => {
    switch (s) {
      case "completed": return "default";
      case "pending": return "secondary";
      case "failed": return "destructive";
      case "refunded": return "outline";
      default: return "secondary";
    }
  };

  return (
    <div className="space-y-4">
      <SectionHeader title="Contributions" description="All chip-in payments across cards." count={count} />

      <div className="grid grid-cols-3 gap-2">
        <MiniStat label="Total Collected" value={`${currency} ${totalCollected.toFixed(0)}`} />
        <MiniStat label="Completed" value={completedCount} />
        <MiniStat label="All Transactions" value={count} />
      </div>

      <div className="relative">
        <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
        <Input
          placeholder="Search by name or email…"
          value={search}
          onChange={e => { setSearch(e.target.value); setPage(0); }}
          className="pl-9"
        />
      </div>

      {loading ? (
        <TableSkeleton rows={8} columns={5} />
      ) : contributions.length === 0 ? (
        <div className="text-center py-8 text-muted-foreground">No contributions found</div>
      ) : isMobile ? (
        <div className="space-y-2">
          {contributions.map(c => (
            <Card key={c.id} className="p-3">
              <div className="flex items-center justify-between gap-2 mb-1.5">
                <p className="font-medium text-sm truncate flex-1">{c.contributor_name}</p>
                <span className="font-semibold text-sm shrink-0">{currency} {Number(c.amount).toFixed(2)}</span>
              </div>
              <div className="flex items-center justify-between gap-2">
                <p className="text-xs text-muted-foreground truncate flex-1">{c.contributor_email || "—"}</p>
                <Badge variant={statusColor(c.status)} className="text-[10px] px-1.5 py-0 shrink-0 capitalize">{c.status}</Badge>
              </div>
              <div className="flex items-center justify-between mt-2 pt-2 border-t border-border/50 text-[11px] text-muted-foreground">
                <p className="truncate flex-1">Card: {cardMap[c.collection_id] || "—"}</p>
                <p className="shrink-0">{format(new Date(c.created_at), "MMM d, yyyy")}</p>
              </div>
            </Card>
          ))}
        </div>
      ) : (
        <Card>
          <CardContent className="p-0 overflow-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Contributor</TableHead>
                  <TableHead>Card</TableHead>
                  <TableHead>Amount</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Date</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {contributions.map(c => (
                  <TableRow key={c.id}>
                    <TableCell>
                      <div className="font-medium">{c.contributor_name}</div>
                      <div className="text-xs text-muted-foreground">{c.contributor_email || "—"}</div>
                    </TableCell>
                    <TableCell className="text-sm">{cardMap[c.collection_id] || "—"}</TableCell>
                    <TableCell>{currency} {Number(c.amount).toFixed(2)}</TableCell>
                    <TableCell><Badge variant={statusColor(c.status)}>{c.status}</Badge></TableCell>
                    <TableCell className="text-sm">{format(new Date(c.created_at), "MMM d, yyyy")}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}

      <Pagination page={page} setPage={setPage} total={count} pageSize={PAGE_SIZE} />
    </div>
  );
};

/* ── Emails Tab ── */
const EmailsTab = () => {
  const isMobile = useIsMobile();
  const [emails, setEmails] = useState<any[]>([]);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [count, setCount] = useState(0);
  const [page, setPage] = useState(0);
  const [statusCounts, setStatusCounts] = useState<Record<string, number>>({});

  const fetchEmails = async () => {
    setLoading(true);
    let query = supabase
      .from("email_send_log")
      .select("*", { count: "exact" })
      .order("created_at", { ascending: false })
      .range(page * PAGE_SIZE, (page + 1) * PAGE_SIZE - 1);

    if (search) {
      query = query.or(`recipient_email.ilike.%${search}%,template_name.ilike.%${search}%`);
    }

    const { data, count: c } = await query;
    setEmails(data || []);
    setCount(c || 0);

    const counts: Record<string, number> = {};
    (data || []).forEach((d: any) => { counts[d.status] = (counts[d.status] || 0) + 1; });
    setStatusCounts(counts);

    setLoading(false);
  };

  useEffect(() => { fetchEmails(); }, [page, search]);

  const statusColor = (s: string) => {
    switch (s) {
      case "sent": return "default";
      case "pending": return "secondary";
      case "failed": case "dlq": return "destructive";
      case "suppressed": return "outline";
      default: return "secondary";
    }
  };

  return (
    <div className="space-y-4">
      <SectionHeader title="Email Logs" description="All transactional and auth emails sent." count={count} />

      <div className="grid grid-cols-4 gap-2">
        <MiniStat label="Sent" value={statusCounts.sent || 0} />
        <MiniStat label="Pending" value={statusCounts.pending || 0} />
        <MiniStat label="Failed" value={(statusCounts.failed || 0) + (statusCounts.dlq || 0)} />
        <MiniStat label="Suppressed" value={statusCounts.suppressed || 0} />
      </div>

      <div className="relative">
        <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
        <Input
          placeholder="Search by email or template…"
          value={search}
          onChange={e => { setSearch(e.target.value); setPage(0); }}
          className="pl-9"
        />
      </div>

      {loading ? (
        <TableSkeleton rows={8} columns={4} />
      ) : emails.length === 0 ? (
        <div className="text-center py-8 text-muted-foreground">No email logs found</div>
      ) : isMobile ? (
        <div className="space-y-2">
          {emails.map(e => (
            <Card key={e.id} className="p-3">
              <div className="flex items-center justify-between gap-2 mb-1.5">
                <p className="font-medium text-sm truncate flex-1">{e.template_name}</p>
                <Badge variant={statusColor(e.status)} className="text-[10px] px-1.5 py-0 shrink-0 capitalize">{e.status}</Badge>
              </div>
              <div className="flex items-center justify-between gap-2 text-xs text-muted-foreground">
                <p className="truncate flex-1">{e.recipient_email}</p>
                <p className="shrink-0 text-[11px]">{format(new Date(e.created_at), "MMM d, HH:mm")}</p>
              </div>
              {e.error_message && (
                <p className="text-xs text-destructive mt-2 pt-2 border-t border-border/50 line-clamp-2">{e.error_message}</p>
              )}
            </Card>
          ))}
        </div>
      ) : (
        <Card>
          <CardContent className="p-0 overflow-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Template</TableHead>
                  <TableHead>Recipient</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Date</TableHead>
                  <TableHead>Error</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {emails.map(e => (
                  <TableRow key={e.id}>
                    <TableCell className="font-medium text-sm">{e.template_name}</TableCell>
                    <TableCell className="text-muted-foreground text-sm">{e.recipient_email}</TableCell>
                    <TableCell><Badge variant={statusColor(e.status)}>{e.status}</Badge></TableCell>
                    <TableCell className="text-sm">{format(new Date(e.created_at), "MMM d, HH:mm")}</TableCell>
                    <TableCell className="text-xs text-destructive max-w-[200px] truncate">{e.error_message || "—"}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}

      <Pagination page={page} setPage={setPage} total={count} pageSize={PAGE_SIZE} />
    </div>
  );
};

/* ── Shared Components ── */

const Pagination = ({ page, setPage, total, pageSize }: { page: number; setPage: (p: number) => void; total: number; pageSize: number }) => {
  const totalPages = Math.ceil(total / pageSize);
  if (totalPages <= 1) return null;

  return (
    <div className="flex items-center justify-between">
      <p className="text-sm text-muted-foreground">Page {page + 1} of {totalPages}</p>
      <div className="flex gap-2">
        <Button variant="outline" size="sm" disabled={page === 0} onClick={() => setPage(page - 1)}>Previous</Button>
        <Button variant="outline" size="sm" disabled={page >= totalPages - 1} onClick={() => setPage(page + 1)}>Next</Button>
      </div>
    </div>
  );
};

export default Admin;
